/**
 * Colorful bet chip with a "fly to pot" exit animation.
 *
 * When `amount` drops from >0 to 0 (street advanced → contributions
 * cleared), the component keeps rendering a ghost chip for 600ms with
 * `.chip-fly-*` class playing so the chip visually dissolves toward
 * the pot instead of just vanishing.
 *
 * `variant` determines the fly direction:
 *   - 'opponent': chips flow DOWN toward the center pot.
 *   - 'hero': chips flow UP toward the center pot (hero is below felt).
 */
import { useEffect, useRef, useState } from 'react';
import { chipClassesFor } from '@/utils/chipColor';

export type BetChipProps = {
  amount: number;
  variant?: 'hero' | 'opponent';
  testId?: string;
};

const EXIT_MS = 600;

export function BetChip({ amount, variant = 'opponent', testId }: BetChipProps) {
  const prev = useRef(amount);
  const [exitingAmount, setExitingAmount] = useState<number | null>(null);

  useEffect(() => {
    if (prev.current > 0 && amount === 0) {
      setExitingAmount(prev.current);
      const id = window.setTimeout(() => setExitingAmount(null), EXIT_MS);
      return () => {
        window.clearTimeout(id);
      };
    }
    prev.current = amount;
    return undefined;
  }, [amount]);

  if (amount > 0) {
    const { bg, text, ring } = chipClassesFor(amount);
    return (
      <span
        className={`font-retro text-base ${bg} ${text} ${ring} rounded-pill px-2.5 py-0.5 tabular-nums font-bold`}
        data-testid={testId}
        data-chip-amount={amount}
      >
        {amount}
      </span>
    );
  }
  if (exitingAmount !== null) {
    const { bg, text, ring } = chipClassesFor(exitingAmount);
    const fly = variant === 'hero' ? 'chip-fly-up' : 'chip-fly-down';
    return (
      <span
        aria-hidden="true"
        className={`font-retro text-base ${bg} ${text} ${ring} rounded-pill px-2.5 py-0.5 tabular-nums font-bold pointer-events-none ${fly}`}
        data-chip-exiting={exitingAmount}
      >
        {exitingAmount}
      </span>
    );
  }
  return null;
}
